import { useApolloClient, useMutation } from '@apollo/client'
import classnames from 'classnames'
import { useContext, useState } from 'react'

import { useAuth } from '../utils/auth'
import { errorMessage } from '../utils/errorMessage'
import { t } from '../utils/i18n'
import { useToasts } from './complex/Toast'
import styles from './_pcoButton.module.scss'
import planingCenterIcon from '../../public/images/planning-center-black@2x.svg'
import { ChordItem, chordItemsFromCharts } from '../utils/songs/songTabsCommon'
import Image from 'next/legacy/image'
import { ModalsContext } from '../utils/modals'
import { withLogin } from '../utils/auth/withLogin'
import {
  SONG_CHORDS_QUERY,
  SONG_DETAILS_QUERY,
  ADD_PCO_SONG_ARRANGEMENT
} from '../gql'
import { ChordSection, Song } from '../__generated__/graphql'

export interface SongSection {
  name: string
}

export interface AddToPcoButtonProps {
  inList: boolean
  song: Song
  songStructure?: Array<SongSection>
  songKey: string
  chordCharts?: ChordItem[]
  capo?: string
}

const AddToPcoButton: React.FC<AddToPcoButtonProps> = ({
  inList,
  song,
  songKey,
  songStructure = [],
  chordCharts = [],
  capo = '0'
}) => {
  const { currentUser, refetchCurrentUser } = useAuth()
  const [enabledPcoButton, setEnabledPcoButton] = useState(true)
  const [alreadyAdded, setAlreadyAdded] = useState(
    Boolean(song?.userSetup?.songPcoInfo?.length)
  )
  const apollo = useApolloClient()
  const { openModal, closeModal } = useContext(ModalsContext)

  const { addToast } = useToasts()
  const handleError = (error: any) => {
    const message = errorMessage(error)
    setEnabledPcoButton(true)
    addToast(message, 'danger')
    refetchCurrentUser()
  }

  const extractNames = (arrayOfObj) => {
    return arrayOfObj.map((item) => {
      return { name: item.name }
    })
  }

  const loadSongStructure = async () => {
    const { data: userSettings } = await apollo.query({
      query: SONG_DETAILS_QUERY,
      variables: {
        id: song.id
      },
      fetchPolicy: 'no-cache'
    })
    const capo = userSettings?.song?.userSetup?.capo || '0'

    const { data: songChordsData } = await apollo.query({
      query: SONG_CHORDS_QUERY,
      variables: {
        songId: song.id,
        key: songKey,
        capo: Number(capo)
      }
    })

    const chordCharts = chordItemsFromCharts(
      songChordsData.chordCharts as ChordSection[],
      userSettings.song?.userSetup?.charts
    )

    return {
      songStructure: extractNames(chordCharts),
      chordCharts: chordCharts,
      capo: capo
    }
  }

  const actualizeSongData = () => {
    if (songStructure.length && chordCharts.length && capo.length) {
      return new Promise((resolve) =>
        resolve({
          songStructure: songStructure,
          chordCharts: chordCharts,
          capo: capo
        })
      )
    } else {
      return loadSongStructure()
    }
  }

  const [addSongArrangementPco] = useMutation<{ songId: number }>(
    ADD_PCO_SONG_ARRANGEMENT,
    {
      onError: (error) => handleError(error),
      onCompleted: () => {
        addToast(
          t(
            `${song.title} was successfully added to your Planning Center Online account`
          )
        )
        setEnabledPcoButton(true)
        setAlreadyAdded(true)
        refetchCurrentUser()
      }
    }
  )

  const handleClick = () => {
    setEnabledPcoButton(false)
    actualizeSongData().then(
      (songData: {
        songStructure: any
        chordCharts: ChordItem[]
        capo: string
      }) => {
        addSongArrangementPco({
          variables: {
            songId: song.id,
            songStructure: songData.songStructure,
            songKey: songKey,
            chordCharts: songData.chordCharts,
            capo: songData.capo
          }
        })
      }
    )
  }

  if (!song.ccliNumber) {
    return <></>
  }

  return (
    <button
      onClick={() => {
        withLogin(handleClick, currentUser, openModal, closeModal)
      }}
      disabled={!enabledPcoButton}
      className={
        inList
          ? classnames(
              styles.inList,
              styles.tooltip,
              alreadyAdded && styles.purple
            )
          : classnames(
              'button',
              'is-wide',
              alreadyAdded ? 'is-purple' : 'is-transparent',
              !enabledPcoButton && styles.disabled
            )
      }
    >
      <div className={styles.iconContainer}>
        <Image
          src={planingCenterIcon}
          className={classnames(
            styles.planingCenterIcon,
            !enabledPcoButton && styles.disabled
          )}
          alt={t(`Add ${song.title} to Planning Center Online`)}
          height={40}
          width={40}
          layout="fixed"
        />
      </div>
      {inList ? (
        <div className={styles.tooltipInfoBox}>
          <div className={styles.tooltipText}>
            {t('Add to Planning Center Online')}
          </div>
        </div>
      ) : (
        t('Add to PCO')
      )}
    </button>
  )
}
export default AddToPcoButton
